<script setup lang="ts">
import { ISeparatedItem } from '@caks/shared/modules/i'
import { IconArrow, IconCross } from '@caks/uikit/icon/'
import { useAccountStore } from '../../store/store.account'
import { InputField } from '@caks/uikit/input'
import { ref } from 'vue'
import { IconSearch } from '@caks/uikit/icon/'
import { postPromoValidation } from '@caks/shared/api/user.route'
import { PromoCode } from '@caks/shared/types/types.user'
import { getWordEnding } from '@caks/shared/utils/formatHelpers'
import { usePromoDiscount } from '../../hooks/usePromo'
import { Keyboard } from '@capacitor/keyboard'
import { Capacitor } from '@capacitor/core'

interface Props {
  desktop?: boolean
  promoConditions?: string | null
  errorText: string
  addressNotSelected: boolean
}
interface Emit {
  (emit: 'open-promo'): void
  (emit: 'open-address'): void
  (emit: 'after-validate-promo', promo: PromoCode): void
}

const store = useAccountStore()
const isPromoModalActive = ref(false)
const props = defineProps<Props>()
const emit = defineEmits<Emit>()
const route = useRoute()

const { totalPrice } = usePromoDiscount()

const openPromoInput = () => {
  if (!store.promo) {
    store.noFooter = true
    isPromoModalActive.value = true
    emit('open-promo')
    //@ts-ignore
    route.query.modal = true
  }
}
const openAddressPicker = () => emit('open-address')
const itemsAmount = computed(() => {
  return store.cart.reduce((accum, item) => {
    return (accum += item.amount)
  }, 0)
})

const getDiscount = (promo: PromoCode): number | 'free-delivery' => {
  let result: number | 'free-delivery' = promo.sale_amount

  if (promo.activation_rule === 'free_delivery') {
    result = 'free-delivery'
  }

  return result
}

const getPriceWithDiscount = (price: number, discount: number) => {
  return Math.floor((price * discount) / 100)
}

const totalInfo = computed(() => {
  const info: Array<{
    name: string
    value: string | number
    unit: string
  }> = [
    {
      name: `${itemsAmount.value} товар${getWordEnding(itemsAmount.value)} на сумму`,
      value: totalPrice.value.sum,
      unit: '₽',
    },
  ]

  if (store.promo) {
    const discount = getDiscount(store.promo)

    if (store.promo.min_amount < totalPrice.value.sum) {
      if (discount === 'free-delivery') {
        if (localStorage.getItem('order') === 'delivery') {
          info.push({
            name: 'Доставка',
            value: 'Бесплатно',
            unit: '',
          })
        }
      } else {
        if (store.promo.activation_rule === 'cart') {
          const isPercents = store.promo.activation_type === 'percents'

          const discountValue = isPercents
            ? getPriceWithDiscount(totalPrice.value.sum, discount)
            : discount

          info.push({
            name: 'Скидка',
            value: `-${discountValue}`,
            unit: '₽',
          })
        }
      }
    }
  }

  return info
})

const promocode = ref('')
const checkedPromocode = ref<null | PromoCode>(store.promo || null)
const invalidPromo = ref(false)

const isDelivery = () => localStorage.getItem('order') === 'delivery'

const closePromoModal = async () => {
  try {
    invalidPromo.value = false
    const res = await postPromoValidation(promocode.value)

    if (res.status === 'ERR') throw res.message

    store.noFooter = false
    isPromoModalActive.value = false
    checkedPromocode.value = res.data

    store.setPromo(res.data)
    localStorage.setItem('promo', JSON.stringify(res.data))
    emit('after-validate-promo', checkedPromocode.value)
  } catch (error) {
    store.noFooter = false
    invalidPromo.value = true
  }
}

watch(
  () => store.promo,
  (newValue) => {
    if (props.desktop && newValue) {
      checkedPromocode.value = newValue
    }
  }
)

const promoText = computed(() => {
  return checkedPromocode.value ? checkedPromocode.value.code : 'Промокод'
})

const removePromo = () => {
  promocode.value = ''
  store.promo = undefined
  checkedPromocode.value = null
}

const keyboardHeight = ref(0)
const pb = computed(() => `${keyboardHeight.value}px`)

watch(isPromoModalActive, () => {
  if (!isPromoModalActive.value) {
    store.noFooter = false
  }
})

onMounted(async () => {
  if (Capacitor.isNativePlatform()) {
    Keyboard.addListener('keyboardWillShow', (info) => {
      keyboardHeight.value = info.keyboardHeight - 40
      //keyboardHeight.value = info.keyboardHeight
    })

    Keyboard.addListener('keyboardWillHide', () => {
      keyboardHeight.value = 0
    })
  }
})

onUnmounted(() => {
  if (Capacitor.isNativePlatform()) {
    Keyboard.removeAllListeners()
  }
})
</script>

<template>
  <div v-if="!desktop" class="cart__data data">
    <i-separated-item v-if="isDelivery()">
      <div class="data__delivery data__flex">
        <div>Доставка</div>
        <div>{{ store.estimate?.delivery_amount }}₽</div>
      </div>
    </i-separated-item>
    <i-separated-item>
      <div
        class="data__promo data__flex"
        :class="{ data__flex_column: checkedPromocode }"
        @click="openPromoInput"
      >
        <div class="data__promo-text">{{ promoText }}</div>
        <icon-arrow v-if="!checkedPromocode" />
        <div v-else class="data__promo-text data__promo-text_small">
          <button @click.stop="removePromo">
            <icon-cross />
          </button>
          {{ checkedPromocode.description }}
        </div>
      </div>
      <div
        v-if="isPromoModalActive && !desktop"
        class="data__promo-modal"
        @click.self=";[(isPromoModalActive = false), (promocode = '')]"
      >
        <div class="promo-modal-wrapper">
          <input-field
            v-model="promocode"
            focus
            placeholder="Введите промокод"
            :end-icon="IconSearch"
            :no-icons="false"
          />
          <button :disabled="!promocode" @click="closePromoModal">Применить</button>
        </div>
        <p v-if="invalidPromo" class="promo-invalid">Не нашли такой промокод</p>
      </div>
    </i-separated-item>
    <i-separated-item v-if="errorText || addressNotSelected">
      <div v-if="errorText" class="data__condition">
        {{ errorText }}
      </div>
      <div v-if="addressNotSelected" class="data__condition">
        Адрес не выбран или находится вне зоны доставки
      </div>
    </i-separated-item>
    <i-separated-item v-if="store.estimate?.discount_amount">
      <div class="data__discount data__flex">
        <div>Скидка</div>
        <div>-{{ store.estimate.discount_amount }}₽</div>
      </div>
    </i-separated-item>
    <i-separated-item>
      <div class="data__total data__flex">
        <div>Итого</div>
        <div v-if="totalPrice.sum">{{ totalPrice.sum + totalPrice.delivery }}₽</div>
        <skeletor v-else style="height: 16px; width: 64px" />
      </div>
    </i-separated-item>
  </div>
  <div v-else class="cart__data data" :class="{ data_desktop: desktop }">
    <i-separated-item desktop>
      <div class="data__promo data__flex" @click="openPromoInput">
        <div class="data__promo-text">{{ promoText }}</div>
        <icon-arrow v-if="!checkedPromocode" />
        <div v-else class="data__promo-text data__promo-text_small data__promo-text_dt">
          {{ checkedPromocode.description }}
          <button @click.stop="removePromo">
            <icon-cross />
          </button>
        </div>
      </div>
    </i-separated-item>

    <i-separated-item v-if="errorText || addressNotSelected" desktop>
      <div v-if="errorText" class="data__condition">
        {{ errorText }}
      </div>
      <div v-if="addressNotSelected" class="data__condition">
        Адрес не выбран или находится вне зоны доставки
      </div>
    </i-separated-item>
    <i-separated-item v-if="store.estimate?.discount_amount" desktop>
      <div class="data__discount data__flex">
        <div>Скидка</div>
        <div>-{{ store.estimate.discount_amount }}₽</div>
      </div>
    </i-separated-item>
    <i-separated-item desktop>
      <div class="data__address data__flex" @click="openAddressPicker">
        <div v-if="store.selectedLocation?.title" class="data__promo-text">
          {{ store.selectedLocation.title }}
        </div>
        <div v-else style="color: #767676">Выберите адрес</div>
        <icon-arrow />
      </div>
    </i-separated-item>
    <i-separated-item desktop last>
      <div
        v-for="(item, index) in totalInfo"
        class="data__total data__flex"
        :class="{ data__discount: index === 1 }"
      >
        <p>{{ item.name }}</p>
        <p>{{ item.value + item.unit }}</p>
      </div>
    </i-separated-item>
  </div>
</template>

<style scoped lang="scss">
.data {
  @include h4-4;

  &_desktop {
    .data__total {
      margin: 16px 0;
    }
  }

  &__delivery {
    color: $sub-text;
  }

  &__promo {
    cursor: pointer;
    color: $sub-text;
    --svg-col: #{$sub-text};

    position: relative;
  }

  &__promo-text {
    button {
      position: absolute;
      right: 16px;
      top: 50%;

      transform: translateY(-50%);
      -webkit-transform: translateY(-50%);
    }

    &_small {
      @include h7-1;
    }

    &_dt {
      margin-right: 28px;
    }
  }

  &__condition {
    @include h5-2;
    opacity: 0.7;
    color: $primary-red;
    padding: 0 16px;
    white-space: pre-line;
    //max-width: 300px;
  }

  &__promo-modal {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.356);
    display: flex;
    flex-direction: column;
    align-items: end;
    justify-content: end;
    max-height: 100vh;
    z-index: 10;

    .promo-modal-wrapper {
      display: flex;
      align-items: center;
      background-color: $bg-gray;
      padding: 16px;
      padding-bottom: calc(v-bind(pb) + 50px + $safe-area-bottom);
      gap: 16px;
      @include h5-6;
      color: $primary-green;

      transition: padding-bottom 0.2s cubic-bezier(0.17, 0.59, 0.4, 0.77);
    }

    .promo-invalid {
      width: 100%;
      padding: 8px 16px;
      background-color: $white;
      color: #eb0038;

      @include h7-1;
    }

    button:disabled {
      color: $sub-text;
    }

    div {
      width: 100%;
    }
  }

  &__address {
    cursor: pointer;
    color: $primary-text;
  }

  &__discount {
    color: $primary-green !important;
  }

  &__total {
    color: $primary-text;

    &_green {
      color: $primary-green;
    }
  }

  &__button {
    position: fixed;
    bottom: calc(16px + #{$safe_area_bottom} + var(--footer-pad));
    width: calc(100% - 32px);

    &_desktop {
      width: 364px;
      bottom: 32px;
    }
  }

  &__flex {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    padding: 0 16px;

    &_column {
      flex-direction: column;
      align-items: start;
    }
  }
}
</style>
